import { splitRecipients } from '@/utils/internalEmailRecipients';
import { toAttachmentPayload } from './attachDocumentSelection';
import type { AttachmentSelection } from './attachDocumentSelection';
import type {
  InternalEmail,
  InternalEmailPrefill,
  InternalEmailRecipientSeed,
  SendInternalEmailPayload,
} from '@/api/internalEmails';

/** The Formik slice the send slider holds. */
export interface SendInternalEmailValues {
  from?: string | number | null;
  fromUserObject?: { id?: string | number; isSharedEmail?: boolean } | null;
  to?: (string | number)[];
  cc?: (string | number)[];
  bcc?: (string | number)[];
  subject?: string;
  body?: string;
}

/** Rebuilds one recipient row from the prefill's three parallel fields. */
const seedRow = (seed?: InternalEmailRecipientSeed): (string | number)[] => [
  ...(seed?.users ?? []).map((user) => String(user.id)),
  ...(seed?.participant_groups ?? []),
  ...(seed?.org_roles ?? []).map((role) => `org_role:${role.id}`),
];

/**
 * Seeds the slider from the activity's configuration.
 *
 * Roles arrive as the tokens they were configured as, alongside the people they
 * currently resolve to — the sender can drop either before sending.
 */
export const toSendInternalEmailValues = (
  prefill: InternalEmailPrefill | undefined,
  currentUserId?: string | number
): SendInternalEmailValues => ({
  from: prefill?.ats_shared_email?.id ?? prefill?.sender?.id ?? currentUserId,
  fromUserObject: prefill?.ats_shared_email?.id
    ? { ...prefill.ats_shared_email, isSharedEmail: true }
    : (prefill?.sender ?? null),
  to: seedRow(prefill?.to),
  cc: seedRow(prefill?.cc),
  bcc: seedRow(prefill?.bcc),
  subject: prefill?.subject ?? '',
  body: prefill?.body ?? '',
});

/**
 * Seeds the composer from an email already queued, for an edit.
 *
 * Recipients come back as the people the roles resolved to, because that is all
 * the send stores — a role chip the sender picked becomes the individuals it
 * covered at compose time. Saving therefore addresses those people explicitly;
 * the email reaches the same inboxes, but stops tracking the role.
 */
export const toEditInternalEmailValues = (
  email: InternalEmail | undefined,
  currentUserId?: string | number
): SendInternalEmailValues => ({
  from: email?.ats_shared_email?.id ?? email?.sender?.id ?? currentUserId,
  fromUserObject: email?.ats_shared_email?.id
    ? { ...email.ats_shared_email, isSharedEmail: true }
    : (email?.sender ?? null),
  to: (email?.to ?? []).map((user) => String(user.id)),
  cc: (email?.cc ?? []).map((user) => String(user.id)),
  bcc: (email?.bcc ?? []).map((user) => String(user.id)),
  subject: email?.subject ?? '',
  body: email?.body ?? '',
});

interface BuildPayloadArgs {
  values: SendInternalEmailValues;
  applicationId: number | string;
  activityId?: number | string;
  attachments: AttachmentSelection;
  /** Send time, owned by the footer rather than the form. */
  scheduleAt?: string | null;
  timezone?: string | null;
}

/**
 * Maps the form onto the send request.
 *
 * Each recipient row is split back into users, job roles and Kula roles; the
 * roles are only resolved to people server-side, at send time.
 */
export const toSendInternalEmailPayload = ({
  values,
  applicationId,
  activityId,
  attachments,
  scheduleAt = null,
  timezone,
}: BuildPayloadArgs): SendInternalEmailPayload => {
  const isSharedEmail = Boolean(values.fromUserObject?.isSharedEmail);
  const payload: SendInternalEmailPayload = {
    ats_application_id: applicationId,
    ...(activityId ? { ats_job_stage_action_id: activityId } : {}),
    sender_id: isSharedEmail ? null : (values.from ?? null),
    ats_shared_email_id: isSharedEmail
      ? (values.fromUserObject?.id ?? null)
      : null,
    subject: values.subject,
    body: values.body,
    scheduled_at: scheduleAt,
    timezone: scheduleAt ? (timezone ?? null) : null,
    ...toAttachmentPayload(attachments),
  };

  (['to', 'cc', 'bcc'] as const).forEach((prefix) => {
    const { userIds, participantGroups, orgRoleIds } = splitRecipients(
      values[prefix]
    );
    payload[`${prefix}_user_ids`] = userIds;
    payload[`${prefix}_participant_groups`] = participantGroups;
    payload[`${prefix}_org_role_ids`] = orgRoleIds;
  });

  return payload;
};
